import React, { useState } from 'react';
import FastGraphSettingsGrid from './FastGraphSettingsGrid';

const DEFAULT_PARAMS = {
  build_approach: 'composite_parallel',
  max_iterations_per_chunk: 2,
  chunk_retries: 2,
  model: 'gpt-4o',
  max_words_per_chunk: 250,
  chunking_strategy: 'word_based',
  hard_limit_words: 800,
  enable_coverage_validation: true,
  min_coverage_threshold: 70,
  enable_reextraction: true,
  // Fast Graph Engine knobs (precision atomizer + lossless coverage)
  fge_llm_budget: 150,
  fge_coverage_threshold: 70,
  fge_extract_images: true,
  fge_vision_model: 'gpt-4o',
  fge_enable_xref: true,
  fge_enable_coref: true,
  fge_enable_tables: true,
  fge_enable_vocab_forge: true,
  fge_enable_typer_v2: true,
  fge_enable_domain_ontology: true,
};

const PROVIDER_TYPE_LABELS = {
  openai: 'OpenAI',
  do_serverless: 'DO Serverless',
  do_droplet: 'DO Droplet',
  do_dedicated: 'DO Dedicated',
  runpod_pod: 'GPU Pod',
  runpod_serverless: 'RunPod Serverless',
};

const CHUNKING_STRATEGY_OPTIONS = [
  { value: 'word_based', label: 'Word-Based (Simple)' },
  { value: 'structure_aware', label: 'Structure-Aware (FAQ/Lists)' }
];

const BUILD_APPROACH_OPTIONS = [
  { value: 'composite_sequential', label: 'Composite Approach - Sequential' },
  { value: 'composite_parallel', label: 'Composite Approach - Parallel' },
  { value: 'fast_graph_engine_precision', label: 'FGE (Precision Atomizer + Lossless Coverage)' },
  { value: 'fge_v4', label: 'FGE v4 (Optimized - Parallel + Cached)' },
  { value: 'fge_v5', label: 'FGE v5 (Hierarchy-Aware Clustering)' },
  { value: 'fge_v6', label: 'FGE v6 (Quality-Hardened + Entity Dedup)' },
  { value: 'fge_v7', label: 'FGE v7 (V4 Extraction + V6 Quality Stack)' },
  { value: 'fge_v8', label: 'FGE v8 (V7 + Performance Optimized)' },
  { value: 'fge_v9', label: 'FGE v9 (Verbatim Text + Gate Classification + Predicates)' },
  { value: 'fge_v10', label: 'FGE v10 (V9 + Schema Abstraction Layer)' },
  { value: 'fge_v8_cortex', label: 'FGE v8 Cortex — Intelligence Document' },
  { value: 'excel_graph_builder', label: 'Excel Graph Builder' },
];

export { DEFAULT_PARAMS, PROVIDER_TYPE_LABELS, BUILD_APPROACH_OPTIONS, CHUNKING_STRATEGY_OPTIONS };

/**
 * Shared processing settings panel used by BuildGraphPage and BulkBuildPage.
 * Pure presentational component -- all state is managed by the parent.
 */
function ProcessingSettingsPanel({
  globalParams,
  setGlobalParams,
  modelOptions = [],
  onApplyToAll,
  onProcessAll,
  showActions = true,
  processingAll = false,
  hasUnprocessedDocs = false,
  collapsible = true,
  defaultOpen = false,
}) {
  const [showSettings, setShowSettings] = useState(defaultOpen);

  const isFastGraphEngine = globalParams.build_approach === 'fast_graph_engine_precision' || globalParams.build_approach === 'fge_v4' || globalParams.build_approach === 'fge_v5' || globalParams.build_approach === 'fge_v6' || globalParams.build_approach === 'fge_v7' || globalParams.build_approach === 'fge_v8' || globalParams.build_approach === 'fge_v9' || globalParams.build_approach === 'fge_v10' || globalParams.build_approach === 'fge_v8_cortex' || globalParams.build_approach === 'excel_graph_builder';

  const buildApproachRow = (
    <div className="settings-grid">
      <div className="setting-item">
        <label>Build Approach</label>
        <select
          value={globalParams.build_approach || 'composite_parallel'}
          onChange={(e) => setGlobalParams({ ...globalParams, build_approach: e.target.value })}
        >
          {BUILD_APPROACH_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>
        {isFastGraphEngine && (
          <span className="hint">
            FGE: rule-based pipeline (chunking, precision atomization, gates)
            with bounded LLM atomization budget plus a lossless gap-fill pass
            for full character coverage. Pair with Test Graph &gt; "FGE
            Optimized (Compact Render)" for the ~28% smaller LLM payload.
          </span>
        )}
      </div>
    </div>
  );

  const llmGrid = (
    <div className="settings-grid">
      <div className="setting-item">
          <label>Model</label>
          <select
            value={globalParams.model}
            onChange={(e) => setGlobalParams({ ...globalParams, model: e.target.value })}
          >
            {modelOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="setting-item">
          <label>Max Iterations per Chunk</label>
          <input
            type="number"
            min="1"
            max="5"
            value={globalParams.max_iterations_per_chunk}
            onChange={(e) => setGlobalParams({ 
              ...globalParams, 
              max_iterations_per_chunk: parseInt(e.target.value) || 2 
            })}
          />
          <span className="hint">1-5, default 2</span>
        </div>
        <div className="setting-item">
          <label>Chunk Retries</label>
          <input
            type="number"
            min="0"
            max="5"
            value={globalParams.chunk_retries}
            onChange={(e) => setGlobalParams({ 
              ...globalParams, 
              chunk_retries: parseInt(e.target.value) || 2 
            })}
          />
          <span className="hint">0-5, default 2</span>
        </div>
        <div className="setting-item">
          <label>Max Words per Chunk</label>
          <input
            type="number"
            min="100"
            max="500"
            step="50"
            value={globalParams.max_words_per_chunk}
            onChange={(e) => setGlobalParams({ 
              ...globalParams, 
              max_words_per_chunk: parseInt(e.target.value) || 250 
            })}
          />
          <span className="hint">100-500 words (soft limit)</span>
        </div>
        <div className="setting-item">
          <label>Chunking Strategy</label>
          <select
            value={globalParams.chunking_strategy || 'word_based'}
            onChange={(e) => setGlobalParams({ 
              ...globalParams, 
              chunking_strategy: e.target.value 
            })}
            className="chunking-strategy-select"
          >
            {CHUNKING_STRATEGY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <span className="hint">Use Structure-Aware for FAQ/lists</span>
        </div>
        {globalParams.chunking_strategy === 'structure_aware' && (
          <div className="setting-item">
            <label>Hard Limit (words)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="number"
                min="400"
                max="1500"
                step="100"
                value={globalParams.hard_limit_words || 800}
                onChange={(e) => setGlobalParams({ 
                  ...globalParams, 
                  hard_limit_words: parseInt(e.target.value) || 800 
                })}
                disabled={globalParams.hard_limit_words === 0}
                style={globalParams.hard_limit_words === 0 ? { opacity: 0.5 } : {}}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', fontSize: '12px' }}>
                <input
                  type="checkbox"
                  checked={globalParams.hard_limit_words === 0}
                  onChange={(e) => setGlobalParams({
                    ...globalParams,
                    hard_limit_words: e.target.checked ? 0 : 800
                  })}
                />
                No limit
              </label>
            </div>
            <span className="hint">{globalParams.hard_limit_words === 0 ? 'Each top-level section = one chunk (no splitting)' : 'Max before forced split (default 800)'}</span>
          </div>
        )}
        
        <div className="settings-section-header">Coverage Validation</div>
        <div className="setting-item checkbox-item">
          <label>
            <input
              type="checkbox"
              checked={globalParams.enable_coverage_validation !== false}
              onChange={(e) => setGlobalParams({ 
                ...globalParams, 
                enable_coverage_validation: e.target.checked 
              })}
            />
            Enable Coverage Validation
          </label>
          <span className="hint">Validate that all content is captured in nodes</span>
        </div>
        <div className="setting-item">
          <label>Min Coverage Threshold (%)</label>
          <input
            type="number"
            min="50"
            max="95"
            step="5"
            value={globalParams.min_coverage_threshold || 70}
            onChange={(e) => setGlobalParams({ 
              ...globalParams, 
              min_coverage_threshold: parseInt(e.target.value) || 70 
            })}
            disabled={globalParams.enable_coverage_validation === false}
          />
          <span className="hint">Trigger re-extraction below this %</span>
        </div>
        <div className="setting-item checkbox-item">
          <label>
            <input
              type="checkbox"
              checked={globalParams.enable_reextraction !== false}
              onChange={(e) => setGlobalParams({ 
                ...globalParams, 
                enable_reextraction: e.target.checked 
              })}
              disabled={globalParams.enable_coverage_validation === false}
            />
            Enable Re-extraction
          </label>
          <span className="hint">Auto re-extract from low-coverage areas</span>
        </div>
      </div>
  );

  const content = (
    <div className="settings-content">
      {buildApproachRow}
      {isFastGraphEngine ? (
        <>
          <div className="settings-grid">
            <div className="setting-item">
              <label>Model</label>
              <select
                value={globalParams.model}
                onChange={(e) => setGlobalParams({ ...globalParams, model: e.target.value })}
              >
                {modelOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          <FastGraphSettingsGrid
            globalParams={globalParams}
            setGlobalParams={setGlobalParams}
          />
        </>
      ) : (
        llmGrid
      )}
      {showActions && (
        <div className="settings-actions">
          {onApplyToAll && (
            <button className="btn-secondary" onClick={onApplyToAll}>
              Apply to All Documents
            </button>
          )}
          {onProcessAll && (
            <button 
              className="btn-primary" 
              onClick={onProcessAll}
              disabled={processingAll || !hasUnprocessedDocs}
            >
              {processingAll ? 'Processing...' : 'Process All Documents'}
            </button>
          )}
        </div>
      )}
    </div>
  );

  if (!collapsible) {
    return (
      <div className="processing-settings-panel">
        <div className="settings-header">
          <h3>Processing Settings</h3>
        </div>
        {content}
      </div>
    );
  }

  return (
    <div className="processing-settings-panel">
      <div className="settings-header" onClick={() => setShowSettings(!showSettings)}>
        <h3>Processing Settings</h3>
        <span className="toggle-icon">{showSettings ? '▼' : '▶'}</span>
      </div>
      {showSettings && content}
    </div>
  );
}

export default ProcessingSettingsPanel;
